import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Archive, AlertCircle, CheckCircle2, Eye, Loader2, ShieldCheck, Trash2, UserX, XCircle,
} from 'lucide-react';
import Modal from '../ui/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useData } from '../../context/AppContext';
import { formatCurrencyTR } from '../../utils/currencyUtils';
import { filterMyStudents } from '../../utils/studentLogic';
import {
  buildYearOptions, groupEntriesByPeriod, groupDepartedStudents, getCurrentPeriod,
  parsePeriodId, formatPeriodLabel,
} from '../../utils/paymentPeriods';
import {
  fetchYearPayments, fetchArchivedPayments, deletePeriodPayments, deleteStudentPayments,
} from '../../services/payments';

const REASON_LABEL = {
  deleted: 'Öğrenci silindi',
  unassigned: 'Eğitmenden ayrıldı',
};

const periodRangeLabel = (first, last) => {
  const a = parsePeriodId(first);
  const b = parsePeriodId(last);
  if (!a) return '';
  const start = formatPeriodLabel(a.year, a.month);
  if (!b || first === last) return start;
  return `${start} – ${formatPeriodLabel(b.year, b.month)}`;
};

/**
 * Aylık/yıllık ödeme arşivi.
 * Kayıtlar kalıcıdır; silme yalnızca 2 adımlı onaydan sonra yapılır.
 */
const PaymentArchiveModal = ({
  isOpen, instructor, onSelectPeriod, onClose, showToast,
}) => {
  const requestConfirm = useConfirm();
  const { students, liveDataReady } = useData();
  const instructorId = instructor?.id || null;
  const [selectedYear, setSelectedYear] = useState(() => getCurrentPeriod().year);
  const [entries, setEntries] = useState([]);
  const [archivedEntries, setArchivedEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingPeriodId, setDeletingPeriodId] = useState(null);
  const [deletingStudentId, setDeletingStudentId] = useState(null);
  const loadIdRef = useRef(0);
  const archivedLoadIdRef = useRef(0);

  const currentStudentIds = useMemo(
    () => filterMyStudents(students || [], instructor).map((s) => s.id),
    [students, instructor]
  );

  const loadArchived = useCallback(() => {
    if (!instructorId) return;
    const loadId = ++archivedLoadIdRef.current;
    fetchArchivedPayments({ instructorId })
      .then((data) => {
        if (loadId === archivedLoadIdRef.current) setArchivedEntries(data);
      })
      .catch((err) => {
        console.warn('Ayrılan öğrenci kayıtları yüklenemedi:', err);
        if (loadId === archivedLoadIdRef.current) setArchivedEntries([]);
      });
  }, [instructorId]);

  const loadYear = useCallback(
    (year) => {
      if (!instructorId) return;
      const loadId = ++loadIdRef.current;
      setIsLoading(true);
      setError(null);

      fetchYearPayments({ instructorId, year })
        .then((data) => {
          if (loadId !== loadIdRef.current) return;
          setEntries(data);
        })
        .catch((err) => {
          if (loadId !== loadIdRef.current) return;
          console.error('Ödeme arşivi yüklenemedi:', err);
          setEntries([]);
          setError('Arşiv yüklenemedi. Lütfen tekrar deneyin.');
        })
        .finally(() => {
          if (loadId === loadIdRef.current) setIsLoading(false);
        });
    },
    [instructorId]
  );

  // Modal her açılışta güncel yıla döner
  useEffect(() => {
    if (!isOpen || !instructorId) return;
    setSelectedYear(getCurrentPeriod().year);
  }, [isOpen, instructorId]);

  useEffect(() => {
    if (!isOpen) {
      loadIdRef.current += 1;
      return;
    }
    loadYear(selectedYear);
  }, [isOpen, selectedYear, loadYear]);

  useEffect(() => {
    if (!isOpen) {
      archivedLoadIdRef.current += 1;
      return;
    }
    loadArchived();
  }, [isOpen, loadArchived]);

  const yearOptions = useMemo(() => buildYearOptions(), []);
  const periods = useMemo(() => groupEntriesByPeriod(entries), [entries]);
  // Öğrenci listesi gelmeden hesaplanırsa herkes "ayrılan" görünür ve Sil düğmesi aktif öğrenciye çıkar
  const departed = useMemo(
    () => (liveDataReady === false
      ? []
      : groupDepartedStudents([...archivedEntries, ...entries], currentStudentIds)),
    [archivedEntries, entries, currentStudentIds, liveDataReady]
  );
  const currentPeriod = getCurrentPeriod();
  const isPastPeriod = (period) => (
    period.year < currentPeriod.year
    || (period.year === currentPeriod.year && period.month < currentPeriod.month)
  );

  const handleDeleteStudentRecords = async (group) => {
    if (deletingStudentId) return;
    await requestConfirm({
      title: 'Ayrılan öğrencinin kayıtlarını sil',
      step1Description: `${group.studentName} için tüm yıllardaki ${group.recordCount} ödeme kaydı kalıcı olarak silinecek.`,
      step2Description: 'Son onay: bu öğrencinin ödeme geçmişi arşivden kaldırılacak.',
      confirmLabel: 'Evet, Kalıcı Sil',
      tone: 'danger',
      busyLabel: 'Siliniyor...',
      onConfirm: async () => {
        setDeletingStudentId(group.studentId);
        try {
          const removed = await deleteStudentPayments({ instructorId, studentId: group.studentId });
          showToast(`${group.studentName}: ${removed} ödeme kaydı silindi.`);
          loadArchived();
          loadYear(selectedYear);
        } catch (err) {
          console.error('Öğrenci kayıtları silinemedi:', err);
          showToast(err.message || 'Kayıtlar silinemedi.', 'error');
          throw err;
        } finally {
          setDeletingStudentId(null);
        }
      },
    });
  };

  const handleDeletePeriod = async (period) => {
    if (deletingPeriodId) return;
    const ok = await requestConfirm({
      title: 'Dönemi sil',
      step1Description: `${period.label} dönemindeki ${period.total} kayıt kalıcı olarak silinecek. Bu işlem geri alınamaz.`,
      step2Description: 'Son onay: dönem kayıtlarını kalıcı olarak silmek için onaylayın.',
      confirmLabel: 'Evet, Kalıcı Sil',
      tone: 'danger',
      busyLabel: 'Siliniyor...',
      onConfirm: async () => {
        setDeletingPeriodId(period.periodId);
        try {
          const removed = await deletePeriodPayments({
            instructorId,
            year: period.year,
            month: period.month,
          });
          showToast(`${period.label} dönemine ait ${removed} ödeme kaydı silindi.`);
          loadYear(selectedYear);
        } catch (err) {
          console.error('Dönem silinemedi:', err);
          showToast(err.message || 'Dönem silinemedi.', 'error');
          throw err;
        } finally {
          setDeletingPeriodId(null);
        }
      },
    });
    if (!ok) return;
  };

  return (
    <Modal title="Ödeme Arşivi" isOpen={isOpen} onClose={onClose}>
      <div className="space-y-5">
        <div className="flex items-start gap-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-2xl p-4">
          <ShieldCheck size={18} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
          <p className="text-xs text-emerald-800 dark:text-emerald-300 leading-relaxed">
            Geçmiş ay ve yıllara ait ödeme kayıtları kalıcı olarak saklanır; sistem tarafından
            hiçbir zaman otomatik silinmez. Silinen veya ayrılan öğrencilerin kayıtları da
            aşağıda <strong>Ayrılan öğrenciler</strong> bölümünde kalır. Kayıtlar yalnızca
            onay verdikten sonra, sizin isteğinizle kaldırılır.
          </p>
        </div>

        {departed.length > 0 && (
          <div>
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase flex items-center gap-1.5">
              <UserX size={14} /> Ayrılan öğrenciler ({departed.length})
            </span>
            <div className="space-y-2 mt-2">
              {departed.map((group) => (
                <div
                  key={group.studentId}
                  className="border border-slate-200 dark:border-slate-700 rounded-2xl p-3 bg-slate-50/60 dark:bg-slate-900/40 flex items-center gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-sm text-slate-800 dark:text-white truncate">{group.studentName}</p>
                    <p className="text-[11px] text-slate-400 truncate">
                      {[REASON_LABEL[group.reason], periodRangeLabel(group.firstPeriod, group.lastPeriod), `${group.recordCount} kayıt`]
                        .filter(Boolean).join(' • ')}
                    </p>
                    <p className="text-[11px] font-bold mt-0.5">
                      <span className="text-emerald-600 dark:text-emerald-400">{formatCurrencyTR(group.collected)} tahsil</span>
                      {group.outstanding > 0 && (
                        <span className="text-red-500 dark:text-red-400"> • {formatCurrencyTR(group.outstanding)} borç</span>
                      )}
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={deletingStudentId === group.studentId}
                    onClick={() => handleDeleteStudentRecords(group)}
                    aria-label={`${group.studentName} kayıtlarını sil`}
                    className="px-3 py-2 rounded-xl text-xs font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors flex items-center gap-1.5 disabled:opacity-60 shrink-0"
                  >
                    {deletingStudentId === group.studentId
                      ? <Loader2 size={14} className="animate-spin" />
                      : <Trash2 size={14} />}
                    Sil
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">
            Arşiv Yılı
          </span>
          <div className="flex flex-wrap gap-2 mt-2">
            {yearOptions.map((year) => (
              <button
                key={year}
                type="button"
                onClick={() => {
                  setSelectedYear(year);
                }}
                aria-pressed={selectedYear === year}
                className={`px-4 py-2 rounded-xl text-sm font-black border transition-all ${
                  selectedYear === year
                    ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                    : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-blue-300'
                }`}
              >
                {year}
              </button>
            ))}
          </div>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center gap-3 py-12 text-slate-400">
            <Loader2 size={20} className="animate-spin" />
            <span className="font-bold text-sm">Arşiv yükleniyor...</span>
          </div>
        )}

        {error && !isLoading && (
          <div className="text-center py-10 space-y-3">
            <AlertCircle size={32} className="mx-auto text-red-400" />
            <p className="text-sm font-bold text-slate-600 dark:text-slate-300">{error}</p>
            <button
              type="button"
              onClick={() => loadYear(selectedYear)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-2.5 rounded-xl text-sm transition-colors"
            >
              Tekrar Dene
            </button>
          </div>
        )}

        {!isLoading && !error && periods.length === 0 && (
          <div className="text-center py-12">
            <Archive size={36} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
            <p className="text-slate-400 font-bold text-sm">
              {selectedYear} yılına ait ödeme kaydı bulunmuyor.
            </p>
          </div>
        )}

        {!isLoading && !error && periods.length > 0 && (
          <div className="space-y-3">
            {periods.map((period) => {
              const isDeleting = deletingPeriodId === period.periodId;
              return (
                <div
                  key={period.periodId}
                  className="border border-slate-200 dark:border-slate-700 rounded-2xl p-4 bg-white dark:bg-slate-900/40"
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-black text-slate-800 dark:text-white">{period.label}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {period.total} kayıt • {formatCurrencyTR(period.collected)} tahsilat
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-xs font-black">
                      <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 size={14} /> {period.paid}
                      </span>
                      <span className="inline-flex items-center gap-1 text-red-500 dark:text-red-400">
                        <XCircle size={14} /> {period.unpaid}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => {
                        onSelectPeriod({ year: period.year, month: period.month });
                        onClose();
                      }}
                      className="flex-1 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 py-2.5 rounded-xl text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-1.5"
                    >
                      <Eye size={14} /> Tabloda Aç
                    </button>
                    {isPastPeriod(period) && (
                    <button
                      type="button"
                      disabled={isDeleting}
                      onClick={() => handleDeletePeriod(period)}
                      className="px-4 py-2.5 rounded-xl text-xs font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors flex items-center gap-1.5 disabled:opacity-60"
                    >
                      {isDeleting ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Trash2 size={14} />
                      )}
                      Dönemi Sil
                    </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PaymentArchiveModal;
